import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const { setAuthState, user } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailFromParams = searchParams.get('email');
  const redirectParam = searchParams.get('redirect');
  // Only allow internal redirects to avoid open-redirect issues
  const safeRedirect =
    redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
      ? redirectParam
      : '/dashboard';
  // Get email from params, or from logged-in user, or empty string
  const email = emailFromParams || user?.email || '';
  const isEmailDisabled = !!email; // Disable if email is provided
  const [isLoading, setIsLoading] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(!!token);
  const [isVerified, setIsVerified] = React.useState(false);
  const [isResending, setIsResending] = React.useState(false);
  // Where to send the user once verification completes. Defaults to the safe
  // redirect, but is upgraded to /cart when the user already has cart items.
  const [destination, setDestination] = React.useState(safeRedirect);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: email
    }
  });

  // Auto-verify if token is present
  React.useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    setIsVerifying(true);
    setIsLoading(true);
    try {
      const response = await api.get('/api/auth/verify-email', {
        params: { token: verificationToken }
      });
      
      const { token: jwtToken, user } = response.data;
      
      // Store token and update auth context
      setAuthState(user, jwtToken);

      // If the user already has items in their cart (e.g. an admin-built cart),
      // take them straight there after verification instead of the dashboard.
      let target = safeRedirect;
      try {
        const cartRes = await api.get('/api/cart');
        if ((cartRes.data?.cart?.items?.length || 0) > 0) {
          target = '/cart';
        }
      } catch (cartError) {
        // Non-fatal: fall back to the default redirect
      }
      setDestination(target);

      setIsVerified(true);
      toast.success('Email verified successfully!');
      
      setTimeout(() => {
        navigate(target);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to verify email. The link may have expired.');
      setIsVerifying(false);
    } finally {
      setIsLoading(false);
    }
  };

  const onResendVerification = async (data) => {
    setIsResending(true);
    try {
      await api.post('/api/auth/resend-verification', {
        email: data.email
      });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  if (isVerifying && !isVerified) {
    return (
      <>
        <Helmet>
          <title>Verifying Email - The Host Bros</title>
        </Helmet>

        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Verifying Your Email</h3>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we verify your email address...
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isVerified) {
    return (
      <>
        <Helmet>
          <title>Email Verified - The Host Bros</title>
        </Helmet>

        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Email Verified Successfully</h3>
              <p className="mt-2 text-sm text-gray-600">
                Your email has been verified. {destination === '/cart' ? 'Redirecting to your cart...' : 'Redirecting to dashboard...'}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Verify Your Email - The Host Bros</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">HB</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {email 
              ? `Please verify your email address to continue.`
              : 'Please verify your email address to access your account.'}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Email Verification Required
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      You must verify your email address before you can access your account. 
                      Check your inbox for the verification email, or request a new one below.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit(onResendVerification)}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    disabled={isEmailDisabled}
                    className={`input-field ${errors.email ? 'border-red-300' : ''} ${isEmailDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Enter your email address"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isResending}
                  className="btn-primary w-full flex justify-center items-center"
                >
                  {isResending ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Resend Verification Email'
                  )}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Back to login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyEmailPage;

